import DOMPurify from 'dompurify';

/**
 * Security utilities for input sanitization and validation
 */

/**
 * Sanitize HTML content to prevent XSS attacks
 */
export function sanitizeHTML(input: string): string {
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br'],
    ALLOWED_ATTR: []
  });
}

/**
 * Sanitize plain text input for safe storage and display
 */
export function sanitizeText(input: string): string {
  if (!input) return '';
  
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .replace(/&lt;script|&lt;\/script/gi, '') // Remove encoded script tags
    .slice(0, 2000); // Limit length to prevent buffer overflow
}

/**
 * Sanitize email content for safe HTML email generation
 */
export function sanitizeForEmail(input: string): string {
  if (!input) return '';
  
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\n/g, '<br>');
}

/**
 * Validate and sanitize contact form data
 */
export interface SanitizedContactData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  message: string;
}

export function sanitizeContactFormData(data: any): SanitizedContactData {
  return {
    firstName: sanitizeText(data.firstName || ''),
    lastName: sanitizeText(data.lastName || ''),
    email: sanitizeText(data.email || ''),
    company: sanitizeText(data.company || ''),
    phone: sanitizeText(data.phone || ''),
    message: sanitizeText(data.message || '')
  };
}

/**
 * Safe error message generation (prevents information disclosure)
 */
export function createSafeErrorMessage(error: unknown, userMessage: string): string {
  // Log the actual error for debugging (server-side only)
  if (typeof window === 'undefined') {
    console.error('Detailed error:', error);
  }
  
  // Return generic user-friendly message
  return userMessage;
}